/**
 * Playback-provider detection, control-URL params, and the origin rules the
 * `postMessage` bridge trusts.
 *
 * DOM-free on purpose: `packages/shared` compiles with `lib: ES2022` and
 * `types: ['node']`, so nothing here may name `window`, `MessageEvent` or an
 * element type. Strings in, strings out — `packages/ui` supplies the browser
 * half and `apps/*` the policy.
 *
 * `resolveRpEmbed` (`replicated-sections.ts`) stays the ONE place that turns an
 * author-supplied share URL into an embeddable `src`, and its output is pinned
 * by its own tests. Everything here sits strictly ON TOP of that output: the
 * control params are injected at the call site, never baked into the resolver.
 */

/** Which `postMessage` dialect (if any) a source URL speaks. */
export type PlaybackProvider = 'youtube' | 'vimeo' | 'file' | 'unknown';

/**
 * Hosts whose iframes speak the YouTube widget protocol. `youtu.be` never
 * serves a player itself, but a share URL that reached us un-normalized is
 * still YouTube — classifying it beats calling it `unknown`.
 */
const YOUTUBE_HOSTS = ['youtube.com', 'youtube-nocookie.com', 'youtu.be'];

/** Hosts whose iframes speak the Vimeo player.js protocol. */
const VIMEO_HOSTS = ['vimeo.com'];

/**
 * Origins each provider may `postMessage` FROM — wider than the detection lists
 * above, because Vimeo serves white-label players from alternate domains and a
 * frame's origin is the only thing a listener can actually check.
 * `youtu.be` is deliberately absent: it redirects, it never hosts a player.
 */
const TRUSTED_ORIGIN_HOSTS: Record<PlaybackProvider, string[]> = {
  youtube: ['youtube.com', 'youtube-nocookie.com'],
  vimeo: [
    'vimeo.com',
    'player.vimeo.com',
    'www.vimeo.com',
    // Vimeo's own white-label / regional player domains.
    'vimeo.work',
    'videoji.hk',
    'videoji.cn',
  ],
  file: [],
  unknown: [],
};

/** Extensions a `<video>` element can take directly as a source. */
const FILE_PATH_RE = /\.(mp4|m4v|webm|ogg|ogv|mov|m3u8)$/i;

/** Exact host or a subdomain of it — NEVER a bare suffix match. */
function hostMatches(host: string, domains: string[]): boolean {
  return domains.some((d) => host === d || host.endsWith(`.${d}`));
}

/**
 * The hosts a tenant allows lesson embeds from, out of the box.
 *
 * Just the two providers whose players we actually drive over postMessage —
 * anything else is an iframe we render but cannot control, which is both a
 * worse player and a wider surface. A tenant that needs another host adds it in
 * Settings; this is a default, not a ceiling.
 */
export const DEFAULT_EMBED_HOSTS = [...YOUTUBE_HOSTS, ...VIMEO_HOSTS];

/**
 * May a lesson embed point at this URL?
 *
 * Two ways to pass: the host is on the tenant's list (exactly, or as a
 * subdomain of a listed domain), or the URL is a direct media FILE over https —
 * an `.mp4` on a tenant's own CDN is served by our own `<video>` element, not by
 * a third party's iframe, so there is no foreign document to allowlist.
 *
 * Plain `http:` is refused outright, even for a file: a mixed-content video on
 * an https page does not play anyway, so accepting it only produces a lesson
 * that looks saved and is silently broken.
 */
export function isAllowedEmbedHost(url: string, hosts: string[]): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  if (
    hostMatches(
      host,
      hosts.map((h) => h.toLowerCase().replace(/^www\./, '')),
    )
  )
    return true;
  return FILE_PATH_RE.test(parsed.pathname);
}

/**
 * Classify a source URL. Only `http(s)` is ever considered: every other scheme
 * (`javascript:`, `data:`, `blob:`) is `unknown`, so no caller can be talked
 * into handing one to a provider adapter.
 *
 * The host test is exact-or-subdomain, which is why `youtube.com.evil.example`
 * — a suffix match under the naive `endsWith('youtube.com')` this replaces — is
 * `unknown` rather than a host we would `postMessage` to.
 */
export function detectPlaybackProvider(src: string): PlaybackProvider {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return 'unknown';
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return 'unknown';
  const host = url.hostname.toLowerCase();
  if (hostMatches(host, YOUTUBE_HOSTS)) return 'youtube';
  if (hostMatches(host, VIMEO_HOSTS)) return 'vimeo';
  if (FILE_PATH_RE.test(url.pathname)) return 'file';
  return 'unknown';
}

/**
 * The iframe `src` that turns provider chrome into a controllable player.
 *
 * YouTube needs `enablejsapi=1` plus an `origin` before it will answer the
 * widget handshake at all; `rel=0` keeps the end card on the same channel.
 * Vimeo needs only `playsinline` — `api=1` / `player_id` are Froogaloop-era
 * and adding them today does nothing.
 *
 * Existing query params are PRESERVED (`?start=` from a shared timestamp,
 * `?h=` from an unlisted Vimeo video); an unparseable src and the
 * `file`/`unknown` providers are returned untouched.
 */
export function controlSrc(
  src: string,
  provider: PlaybackProvider,
  opts: { origin: string; playsinline?: boolean },
): string {
  if (provider !== 'youtube' && provider !== 'vimeo') return src;
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return src;
  }
  url.searchParams.set('playsinline', opts.playsinline === false ? '0' : '1');
  if (provider === 'youtube') {
    url.searchParams.set('enablejsapi', '1');
    // An EMPTY origin is worse than none — YouTube compares it literally and
    // refuses the handshake. A caller with no origin gets the param omitted.
    if (opts.origin) url.searchParams.set('origin', opts.origin);
    url.searchParams.set('rel', '0');
  }
  return url.toString();
}

/**
 * The `targetOrigin` to post commands to — the frame's own origin, upgraded to
 * https (a provider is never served over http; posting to the http origin of an
 * https frame silently drops the message).
 *
 * Falls back to `'*'` only when the src has no usable origin at all, which the
 * adapter never reaches: such a src detects as `unknown` and goes straight to
 * `status: 'unsupported'` without posting anything.
 */
export function providerTargetOrigin(src: string): string {
  try {
    const url = new URL(src);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return '*';
    return `https://${url.host}`;
  } catch {
    return '*';
  }
}

/**
 * Is `origin` on one of `domains` — exact host or a subdomain of it?
 *
 * Moved verbatim from `apps/member/src/components/lesson-player.tsx`. The
 * subdomain rule is the point: a suffix test would accept
 * `https://youtube.com.evil.example`, whose frame could then feed the player
 * arbitrary state. An unparseable origin is rejected.
 */
export function originHostMatches(origin: string, domains: string[]): boolean {
  try {
    const host = new URL(origin).hostname;
    return hostMatches(host, domains);
  } catch {
    return false;
  }
}

/**
 * May a `message` frame claiming to be from `provider` be believed?
 *
 * Requires https — an http origin for an https provider is either a downgrade
 * or a spoof, never the real player — plus an exact-or-subdomain host match.
 * `file` and `unknown` trust nothing: a native `<video>` and a passthrough
 * iframe have no protocol to speak.
 */
export function isTrustedProviderOrigin(provider: PlaybackProvider, origin: string): boolean {
  const domains = TRUSTED_ORIGIN_HOSTS[provider];
  if (domains.length === 0) return false;
  try {
    if (new URL(origin).protocol !== 'https:') return false;
  } catch {
    return false;
  }
  return originHostMatches(origin, domains);
}
